import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { JSONEditorFormItem } from '@/components/ui/form-fields/JsonEditorFormItem';
import GMap from '@/components/ui/GMap';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { ButtonLink } from '@/components/ui/Link';
import { Separator } from '@/components/ui/Separator';
import { Switch } from '@/components/ui/Switch';
import {
  AuditLogByEntityQuery,
  Device,
  DeviceSetInput,
  Roi,
  RoiByDeviceQuery,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import {
  DeviceConfig,
  DeviceState,
  JSONConfig,
  MediaKind,
  Product,
  PRODUCTS,
} from '@/models/device.models';
import { FeatureName, Role } from '@/models/role.models';
import { ClientZone } from '@/models/zone.models';
import { convertCoordToGoogleMapsFormat } from '@/utils/map';
import hasPermission from '@/utils/permission.function';
import { renderDeviceStateBadge } from '@/utils/state';
import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from '@tanstack/react-query';
import centroid from '@turf/centroid';
import { Copy, Ellipsis, Navigation, Pencil, RefreshCw } from 'lucide-react';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { GeoJSONGeometry, parse } from 'wellknown';
import { snZoneQuery } from '../../graphql/query';
import PolygonEditor from '../PolygonEditor';

import { ComboboxFormField } from '@/components/ui/form-fields/ComboboxFormField';
import { getKeycloakRoles } from '@/features/Keycloak/keycloakService';
import { v4 as uuid } from 'uuid';
interface DeviceGeneralTabProps {
  selectedProduct?: Product;
  device: Device;
  deviceRois: Roi[];
  editing: boolean;
  isRefreshingCache?: boolean;
  setEditing: (editing: boolean) => void;
  refetchDeviceRois: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<RoiByDeviceQuery, Error>>;
  refetchAuditLog: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<AuditLogByEntityQuery, Error>>;
  updateDevice: (input: DeviceSetInput) => void;
  onRefreshCache?: () => void;
  onSelectedMediaTimestampChange?: (timestamp?: string) => void;
  onSelectedMediaKindChange?: (kind: MediaKind) => void;
}
type EditDeviceFormData = {
  name: string;
  serial: string;
  products: string[];
  config: JSONConfig;
  state: DeviceState;
};

export function DeviceGeneralTab({
  device,
  selectedProduct,
  deviceRois,
  editing,
  isRefreshingCache,
  setEditing,
  refetchDeviceRois,
  refetchAuditLog,
  updateDevice,
  onRefreshCache,
  onSelectedMediaTimestampChange,
  onSelectedMediaKindChange,
}: DeviceGeneralTabProps) {
  const [deviceState, setDeviceState] = useState<DeviceState>();
  const [mapVisible, setMapVisible] = useState(true);
  const [zoneFetchRadius, setZoneFetchRadius] = useState<number>(50);
  const [mapDisplayItems, setMapDisplayItems] = useState<ClientZone[]>([]);
  const [mapCenterChange, setMapCenterChange] = useState<google.maps.LatLng>();
  const [mapCenter, setMapCenter] = useState<
    google.maps.LatLng | google.maps.LatLngLiteral
  >();
  const {
    register,
    reset,
    getValues,
    getFieldState,
    setValue,
    control,
    setError,
    formState: { isValid, isDirty },
  } = useForm<EditDeviceFormData>({});

  const { data: snZoneData } = useQuery({
    queryKey: ['SnZones', device, mapCenterChange, zoneFetchRadius],
    queryFn: () =>
      execute({
        query: snZoneQuery,
        variables: {
          id: device.id,
          distanceWithinMeters: zoneFetchRadius,
          longitude: mapCenterChange!.lng(),
          latitude: mapCenterChange!.lat(),
        },
      }),
    select: (data) => data,
    retry: false,
    staleTime: 5000,
    enabled: !!mapCenterChange && !!device && !!Array.isArray(device.products),
    meta: { suppressErrorToast: true },
  });

  useEffect(() => {
    if (device) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const state = device.config?.state as DeviceState;
      const formData: EditDeviceFormData = {
        name: device.name as string,
        serial: device.serial,
        products: device.products as string[],
        state,
        config: device.config,
      };
      reset(formData);
      setDeviceState(state);
    }
  }, [device, reset]);

  useEffect(() => {
    let mapItems: ClientZone[] = [];
    if (device?.site) {
      mapItems = [
        {
          key: 'site',
          geometry: device.site.geometry,
        },
      ];
    }
    if (snZoneData) {
      const zones = snZoneData?.snZones?.map((zone) => {
        return {
          key: zone.zoneKey,
          geometry: parse(zone?.polygon) as GeoJSONGeometry,
        };
      });
      mapItems = [...mapItems, ...zones];
    }
    setMapDisplayItems(mapItems);
  }, [snZoneData, device]);

  useEffect(() => {
    if (device?.site?.geometry) {
      const centerPoint = centroid(device.site?.geometry as GeoJSONGeometry);
      const centerLatLng = convertCoordToGoogleMapsFormat(
        centerPoint.geometry?.coordinates,
      );
      setMapCenter(centerLatLng);
    }
  }, [device]);

  const undoChanges = useCallback(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    undoChanges();
    setEditing(false);
  }, [selectedProduct, undoChanges, setEditing]);

  const handleRefetchRoiZonData = async () => {
    await refetchDeviceRois();
    await refetchAuditLog();
  };
  const userRoles = getKeycloakRoles() ?? [];

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { name, config, serial, products, state } = getValues();
    const nameState = getFieldState('name');
    const serialState = getFieldState('serial');
    const productsState = getFieldState('products');
    const configState = getFieldState('config');
    const deviceStateState = getFieldState('state');
    const updateInput: DeviceSetInput = {
      updatedAt: new Date().toISOString(),
      rowVersion: uuid(),
    };
    if (nameState.isDirty) {
      updateInput.name = name;
    }
    if (serialState.isDirty) {
      updateInput.serial = serial;
    }
    if (productsState.isDirty) {
      updateInput.products = products;
    }
    if (configState.isDirty || deviceStateState.isDirty) {
      updateInput.config = config;
    }
    if (deviceStateState.isDirty) {
      updateInput.config = updateInput.config
        ? { ...updateInput.config, state }
        : { config, state };
    }
    updateDevice(updateInput);
  };

  const handleMapCenterChange = (mapCenter: google.maps.LatLng) => {
    setMapCenterChange(mapCenter);
  };

  const handleProductionReadyChange = (
    checked: boolean,
    onChange: (value: boolean) => void,
  ) => {
    if (checked) {
      toast.info(
        'Please verify zone and ROI configurations are set correctly before marking the device as ready for production.',
        {
          duration: 5000,
        },
      );
    }
    onChange(checked);
  };

  return (
    <>
      <div className="flex w-full justify-items-end"></div>

      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
        <div className="grid gap-4">
          <PolygonEditor
            rois={deviceRois}
            device={device}
            onRefetchRoiZonData={() => void handleRefetchRoiZonData()}
            onChildMapVisible={(visible) => {
              setMapVisible(!visible);
            }}
            onSelectedTimestampChange={onSelectedMediaTimestampChange}
            onSelectedMediaKindChange={onSelectedMediaKindChange}
          />

          <div>
            <Card className="relative">
              <CardHeader>
                <CardTitle>Location</CardTitle>
                {device.site ? (
                  <ButtonLink
                    className="absolute top-4 right-4"
                    variant={'outline'}
                    size={'icon'}
                    to={`/sites/${device.siteId}`}
                  >
                    <Navigation />
                  </ButtonLink>
                ) : (
                  <>
                    <ButtonLink
                      to={'/sites'}
                      variant={'outline'}
                      size={'sm'}
                      className="absolute top-4 right-4"
                    >
                      View Sites
                    </ButtonLink>
                    <CardDescription>
                      This device is not assigned to any site.
                    </CardDescription>
                  </>
                )}
              </CardHeader>
              <CardContent>
                {device.site && (
                  <div className="flex flex-col gap-6">
                    <div className="grid gap-2">
                      {mapVisible && (
                        <GMap
                          displayItems={mapDisplayItems}
                          centerCircleRadius={zoneFetchRadius}
                          center={mapCenter}
                          onCenterChange={handleMapCenterChange}
                          onCenterCircleRadiusChange={(radius) =>
                            setZoneFetchRadius(radius)
                          }
                        />
                      )}
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="site">Site name</Label>
                      <Input
                        type="text"
                        id="site"
                        placeholder="Site name"
                        readOnly
                        defaultValue={device.site?.name}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        <div className="grid gap-4">
          <Card className="relative">
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => void handleSubmit(e)}
            >
              <CardHeader>
                <CardTitle>
                  <div>
                    Device Configuration
                    <span className="ms-4">
                      {renderDeviceStateBadge(
                        (device.config as DeviceConfig)?.state,
                      )}
                    </span>
                  </div>
                  {hasPermission(FeatureName.UPDATE_DEVICE) && (
                    <div className="absolute top-4 right-4 flex gap-2">
                      <Button
                        type="button"
                        variant={'outline'}
                        size={'sm'}
                        onClick={() => {
                          setEditing(!editing);
                        }}
                      >
                        {editing ? (
                          'Cancel Editing'
                        ) : (
                          <>
                            <Pencil />
                            Edit
                          </>
                        )}
                      </Button>
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant={'outline'}
                            size={'sm'}
                            aria-label="More actions"
                          >
                            <Ellipsis />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            disabled={isRefreshingCache}
                            onClick={() => {
                              onRefreshCache?.();
                            }}
                          >
                            <RefreshCw />
                            Refresh Cache
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col">
                <div className="flex flex-col gap-6">
                  <div className="grid gap-2">
                    <Label htmlFor="id">Id</Label>

                    <div className="relative">
                      <Input
                        readOnly
                        type="text"
                        id="id"
                        placeholder="Id"
                        defaultValue={device.id}
                        className="truncate pr-8"
                      />
                      <Button
                        type="button"
                        className="text-muted-foreground hover:text-primary absolute top-0 right-0 hover:cursor-pointer"
                        size={'icon'}
                        variant={'link'}
                        onClick={() => {
                          toast.message('Copied!', {
                            duration: 1000,
                          });
                          void navigator.clipboard.writeText(device.id);
                        }}
                      >
                        <Copy className="size-5" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="shortId">Short-Id</Label>

                    <div className="relative">
                      <Input
                        readOnly
                        type="text"
                        id="shortId"
                        placeholder=""
                        defaultValue={device.shortId}
                        className="truncate pr-8"
                      />
                      <Button
                        type="button"
                        className="text-muted-foreground hover:text-primary absolute top-0 right-0 hover:cursor-pointer"
                        size={'icon'}
                        variant={'link'}
                        onClick={() => {
                          toast.message('Copied!', {
                            duration: 1000,
                          });
                          void navigator.clipboard.writeText(device.shortId);
                        }}
                      >
                        <Copy className="size-5" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      readOnly={!editing}
                      type="text"
                      id="name"
                      placeholder="Device name"
                      {...register('name', {
                        required: 'name is required',
                      })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="serial">Serial</Label>
                    <Input
                      type="text"
                      readOnly={!editing}
                      id="serial"
                      placeholder="serial"
                      {...register('serial', {
                        required: 'serial is required',
                      })}
                    />
                  </div>

                  <Controller
                    name="products"
                    control={control}
                    rules={{ required: 'Products is required' }}
                    render={({ field }) => (
                      <div className="grid gap-2">
                        <span className="text-sm leading-none font-medium">
                          Products
                        </span>
                        <ComboboxFormField
                          options={PRODUCTS.map((product) => ({
                            value: product,
                          }))}
                          canEdit={editing}
                          value={field.value}
                          onChange={(value) => {
                            console.log('Selected products:', value);
                            field.onChange(value);
                          }}
                        />
                      </div>
                    )}
                  />

                  {hasPermission(FeatureName.VIEW_DEVICE_CONFIG) && (
                    <div className="flex flex-col gap-2">
                      <span className="text-sm leading-none font-medium capitalize">
                        {selectedProduct} Config
                      </span>

                      {selectedProduct === 'senpic' && (
                        <Controller
                          name="config.Senpic"
                          control={control}
                          render={({ field }) => (
                            <div className="flex flex-1 flex-col gap-2">
                              <div className="flex-1">
                                <JSONEditorFormItem
                                  onValueChange={field.onChange}
                                  value={field.value}
                                  onSetError={() => {
                                    setError('config.Senpic', {
                                      message: 'Invalid JSON!',
                                    });
                                  }}
                                  mode={editing ? 'text' : 'view'}
                                  options={{
                                    navigationBar: true,
                                    mainMenuBar: true,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                      )}
                      {selectedProduct === 'senshield' && (
                        <Controller
                          name="config.Senshield"
                          control={control}
                          render={({ field }) => (
                            <div className="flex flex-1 flex-col gap-2">
                              <div className="flex-1">
                                <JSONEditorFormItem
                                  onValueChange={field.onChange}
                                  value={field.value}
                                  onSetError={() => {
                                    setError('config.Senshield', {
                                      message: 'Invalid JSON!',
                                    });
                                  }}
                                  mode={editing ? 'text' : 'view'}
                                  options={{
                                    navigationBar: true,
                                    mainMenuBar: true,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                      )}
                      {selectedProduct === 'senforce' && (
                        <Controller
                          name="config.Senforce"
                          control={control}
                          render={({ field }) => (
                            <div className="flex flex-1 flex-col gap-2">
                              <div className="flex-1">
                                <JSONEditorFormItem
                                  onValueChange={field.onChange}
                                  value={field.value}
                                  onSetError={() => {
                                    setError('config.Senforce', {
                                      message: 'Invalid JSON!',
                                    });
                                  }}
                                  mode={editing ? 'text' : 'view'}
                                  options={{
                                    navigationBar: true,
                                    mainMenuBar: true,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                      )}
                    </div>
                  )}
                  <Separator />
                  <div className="flex flex-col gap-2">
                    <span className="text-sm leading-none font-medium">
                      Status
                    </span>

                    <div className="flex-1">
                      <div className="flex flex-wrap items-center space-x-6 gap-y-4">
                        {!deviceState?.created && (
                          <Controller
                            name="state.created"
                            control={control}
                            render={({ field }) => (
                              <div className="flex space-x-2">
                                <Switch
                                  id="state.created"
                                  disabled={!editing}
                                  checked={field?.value || false}
                                  onCheckedChange={field.onChange}
                                />
                                <Label htmlFor="state.created">Created</Label>
                              </div>
                            )}
                          />
                        )}

                        {userRoles.includes(Role.INSTALLER) ? (
                          <>
                            {!deviceState?.installed && (
                              <Controller
                                name="state.installed"
                                control={control}
                                render={({ field }) => (
                                  <div className="flex space-x-2">
                                    <Switch
                                      id="state.installed"
                                      checked={field?.value || false}
                                      disabled={
                                        !hasPermission(
                                          FeatureName.UPDATE_DEVICE_INSTALL_STATUS,
                                        )
                                      }
                                      onCheckedChange={(checked) => {
                                        field.onChange(checked);
                                        if (checked) {
                                          setValue(
                                            'state.installationFailed',
                                            false,
                                          );
                                        }
                                      }}
                                    />
                                    <Label htmlFor="state.installed">
                                      Installed
                                    </Label>
                                  </div>
                                )}
                              />
                            )}

                            <Controller
                              name="state.installationFailed"
                              control={control}
                              render={({ field }) => (
                                <div className="flex space-x-2">
                                  <Switch
                                    id="state.installationFailed"
                                    disabled={
                                      !hasPermission(
                                        FeatureName.UPDATE_DEVICE_INSTALL_STATUS,
                                      )
                                    }
                                    checked={field?.value || false}
                                    onCheckedChange={(checked) => {
                                      field.onChange(checked);
                                      if (checked) {
                                        setValue('state.installed', false);
                                      }
                                    }}
                                  />
                                  <Label htmlFor="state.installationFailed">
                                    Install Failed
                                  </Label>
                                </div>
                              )}
                            />
                          </>
                        ) : (
                          <>
                            {!deviceState?.installed && (
                              <Controller
                                name="state.installed"
                                control={control}
                                render={({ field }) => (
                                  <div className="flex space-x-2">
                                    <Switch
                                      id="state.installed"
                                      checked={field?.value || false}
                                      disabled={
                                        !hasPermission(
                                          FeatureName.UPDATE_DEVICE_INSTALL_STATUS,
                                        ) || !editing
                                      }
                                      onCheckedChange={(checked) => {
                                        field.onChange(checked);
                                        if (checked) {
                                          setValue(
                                            'state.installationFailed',
                                            false,
                                          );
                                        }
                                      }}
                                    />
                                    <Label htmlFor="state.installed">
                                      Installed
                                    </Label>
                                  </div>
                                )}
                              />
                            )}

                            <Controller
                              name="state.installationFailed"
                              control={control}
                              render={({ field }) => (
                                <div className="flex space-x-2">
                                  <Switch
                                    id="state.installationFailed"
                                    disabled={
                                      !hasPermission(
                                        FeatureName.UPDATE_DEVICE_INSTALL_STATUS,
                                      ) || !editing
                                    }
                                    checked={field?.value || false}
                                    onCheckedChange={(checked) => {
                                      field.onChange(checked);
                                      if (checked) {
                                        setValue('state.installed', false);
                                      }
                                    }}
                                  />
                                  <Label htmlFor="state.installationFailed">
                                    Install Failed
                                  </Label>
                                </div>
                              )}
                            />

                            <Controller
                              name="state.productionReady"
                              control={control}
                              render={({ field }) => (
                                <div className="flex space-x-2">
                                  <Switch
                                    disabled={
                                      !hasPermission(
                                        FeatureName.UPDATE_DEVICE_PRODUCTION_STATUS,
                                      ) || !editing
                                    }
                                    checked={field?.value || false}
                                    id="state.productionReady"
                                    onCheckedChange={(checked) => {
                                      handleProductionReadyChange(
                                        checked,
                                        field.onChange,
                                      );
                                    }}
                                  />

                                  <Label htmlFor="state.productionReady">
                                    Ready for Production
                                  </Label>
                                </div>
                              )}
                            />

                            <Controller
                              name="state.disabled"
                              control={control}
                              render={({ field }) => (
                                <div className="flex space-x-2">
                                  <Switch
                                    disabled={
                                      !hasPermission(
                                        FeatureName.UPDATE_DEVICE_DISABLED_STATUS,
                                      ) || !editing
                                    }
                                    checked={field?.value || false}
                                    id="state.disabled"
                                    onCheckedChange={field.onChange}
                                  />

                                  <Label htmlFor="state.disabled">
                                    Disabled
                                  </Label>
                                </div>
                              )}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
              {isDirty && (
                <CardFooter>
                  <div className="mt-4 flex w-full justify-end gap-4">
                    <Button
                      disabled={!isValid}
                      variant={'default'}
                      className="w-30"
                      type="submit"
                      size={'sm'}
                    >
                      Save
                    </Button>

                    <Button
                      variant={'outline'}
                      className="w-30"
                      size={'sm'}
                      onClick={undoChanges}
                    >
                      Cancel
                    </Button>
                  </div>
                </CardFooter>
              )}
            </form>
          </Card>
        </div>
      </div>
    </>
  );
}
